import React from "react";
import { toast } from "react-toastify";
import { LanguageConstants } from "../../../constants/LanguageConstants";
import { isQuoteLineUnavailable } from "./helpers";
import type { QuotationLineRow } from "./renderQuotationItemCards";

export type QuotationItemsTableProps = {
  rows: QuotationLineRow[];
  getBrandForRow: (res: any, isPending: boolean) => string;
  canEditRow: boolean;
  anotherBlocks: boolean;
  tableQtyEditingQuoteId: number | string | null;
  isQuotedView: boolean;
  renderQtyControl: (
    res: any,
    kind: "api" | "pending",
    displayNo: number,
    editDis: boolean,
  ) => React.ReactNode;
  formatQuotedMoneyCell: (res: any, which: "unit" | "extended") => string;
  formatQuotedEtaCell: (res: any) => string;
  handleRowQtyEdit: (
    res: any,
    kind: "api" | "pending",
    displayNo: number,
    editDis: boolean,
  ) => void;
  onDeleteLine: (res: any, displayNo: number) => void;
};

export function QuotationItemsTable({
  rows,
  getBrandForRow,
  canEditRow,
  anotherBlocks,
  tableQtyEditingQuoteId,
  isQuotedView,
  renderQtyControl,
  formatQuotedMoneyCell,
  formatQuotedEtaCell,
  handleRowQtyEdit,
  onDeleteLine,
}: QuotationItemsTableProps) {
  return (
    <div className="manual-quote-review-table-wrap">
      <table className="vq__table table manual-quote-table">
        <thead>
          <tr>
            <th className="vq__num">{LanguageConstants?.Item}</th>
            <th>{LanguageConstants?.BRANDNAME ?? LanguageConstants?.BRAND}</th>
            <th>{LanguageConstants?.PART_NUMBER_HASH ?? "Part #"}</th>
            <th className="qty">{LanguageConstants?.QTY_SHORT ?? "Qty"}</th>
            <th className="desc">{LanguageConstants?.Description}</th>
            <th>{LanguageConstants?.SERIALNUMBER}</th>
            <th>{LanguageConstants?.MODELNUMBER}</th>
            <th>{LanguageConstants?.SKU_LABEL ?? "SKU"}</th>
            {isQuotedView ? (
              <>
                <th>{LanguageConstants?.PRE_PRODUCT_PRICE ?? "Unit Price"}</th>
                <th>{LanguageConstants?.EXTED_PRICE ?? "Extended Price"}</th>
                <th>{LanguageConstants?.VIEW_QUOTE_ETA ?? "ETA"}</th>
              </>
            ) : null}
            <th className="vq__actions">{LanguageConstants?.ACTION}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const { res, displayNo, kind } = row;
            const brand = getBrandForRow(res, kind === "pending") || "N/A";
            const editDis = anotherBlocks;
            const qtyUnavailable = isQuoteLineUnavailable(res);
            const qtyEditOpenForThisRow =
              tableQtyEditingQuoteId != null &&
              (kind === "api"
                ? String(tableQtyEditingQuoteId) === String(res?.id)
                : String(tableQtyEditingQuoteId) === `pending-${res?.__pendingAnotherProductIndex ?? ""}`);

            return (
              <tr key={kind === "api" ? `api-${res?.id}` : `pen-${displayNo}-${res?.part_number}`}>
                <td className="vq__num border-0">{displayNo}</td>
                <td className="border-0">{brand}</td>
                <td className="border-0">{res?.part_number}</td>
                <td className="qty-para border-0">{renderQtyControl(res, kind, displayNo, editDis)}</td>
                <td className="border-0 desc-para">{res?.description || "N/A"}</td>
                <td className="border-0">{res?.serial_number || "N/A"}</td>
                <td className="border-0">{res?.model_number || "N/A"}</td>
                <td className="border-0">
                  {res?.sku != null && String(res.sku).trim() !== "" ? String(res.sku).trim() : "N/A"}
                </td>
                {isQuotedView ? (
                  <>
                    <td className="border-0">{formatQuotedMoneyCell(res, "unit")}</td>
                    <td className="border-0">{formatQuotedMoneyCell(res, "extended")}</td>
                    <td className="border-0">{formatQuotedEtaCell(res)}</td>
                  </>
                ) : null}
                <td className="vq__actions border-0">
                  {canEditRow ? (
                    <div className="d-flex align-items-center justify-content-center">
                      <button
                        type="button"
                        className="vq-iconBtn vq-iconBtn--edit"
                        disabled={editDis || qtyUnavailable || qtyEditOpenForThisRow}
                        title={
                          qtyEditOpenForThisRow
                            ? (LanguageConstants?.VIEW_QUOTE_USE_CANCEL_END_EDIT ??
                              "Use Cancel below to stop editing.")
                            : undefined
                        }
                        onClick={() => handleRowQtyEdit(res, kind, displayNo, editDis)}
                      >
                        <i className="icon-edit-profile" />
                      </button>
                      <button
                        type="button"
                        className="vq-iconBtn vq-iconBtn--delete"
                        disabled={editDis}
                        onClick={() => {
                          if (editDis) toast.error(LanguageConstants?.MESSAGE_FOR_PRODUCT);
                          else onDeleteLine(res, displayNo);
                        }}
                      >
                        <i className="icon-delete-2" />
                      </button>
                    </div>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
